import { useQuery } from "@tanstack/react-query";
import { Factory, MoveRight } from "lucide-react";
import { toast } from "sonner";

import { TownCombobox } from "@/components/groups/town-combobox";
import { useAuth } from "@/hooks/use-auth";
import { t } from "@/i18n/pl";
import { queryClient, queryKeys } from "@/lib/query-client";
import { cn } from "@/lib/utils";
import { ApiError } from "@/services/api-client";
import * as authService from "@/services/auth.service";
import * as groupsService from "@/services/groups.service";

/**
 * The seeker's journey in one control strip:
 *
 *   [Twoja miejscowość ▾]  →  [Wszystkie | Ubojnia | Przetwórnia]
 *
 * Origin picks the profile town (drives the "Po drodze" matching); destination
 * chips filter the list by work site. Stacks vertically on narrow phones.
 */
export function JourneyBar({
  plantFilter,
  onPlantFilterChange,
}: {
  plantFilter: string | null;
  onPlantFilterChange: (plantId: string | null) => void;
}) {
  const { user, refreshUser } = useAuth();
  const { data: plants } = useQuery({
    queryKey: ["plants"],
    queryFn: groupsService.listPlants,
    staleTime: Infinity,
  });

  function chip(label: string, value: string | null) {
    const active = plantFilter === value;
    return (
      <button
        key={value ?? "all"}
        type="button"
        aria-pressed={active}
        onClick={() => onPlantFilterChange(value)}
        className={cn(
          "rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors",
          active
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-card hover:bg-accent",
        )}
      >
        {label}
      </button>
    );
  }

  return (
    <div className="bg-accent/40 flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-col gap-1 sm:max-w-64 sm:flex-1">
        <span className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
          {t.home.yourTown}
        </span>
        <TownCombobox
          id="home-town"
          value={user?.home_area ?? null}
          onSelect={(town) => {
            void (async () => {
              try {
                await authService.updateProfile({ home_area_id: town.id });
                await refreshUser();
                void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
              } catch (error) {
                toast.error(error instanceof ApiError ? error.message : t.common.genericError);
              }
            })();
          }}
        />
      </div>

      <MoveRight
        aria-hidden
        className="text-muted-foreground hidden size-5 shrink-0 sm:block"
      />

      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-muted-foreground flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
          <Factory className="size-3" aria-hidden /> {t.home.destinationFilter}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {chip(t.home.allPlants, null)}
          {(plants ?? []).map((plant) => chip(plant.name, plant.id))}
        </div>
      </div>
    </div>
  );
}
